import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from './context/StoreContext';
import Sidebar from './components/Sidebar';
import TopBar from './components/TopBar';
import { BoardView, ListView, TimelineView } from './components/Views';
import Dashboard from './components/Dashboard';
import Settings from './components/Settings';
import BackupVault from './components/BackupVault';
import Editor from './components/Editor';
import CommandPalette from './components/CommandPalette';
import BulkActionBar from './components/BulkActionBar';
import QuickCapture from './components/QuickCapture';
import MobileBottomNav from './components/MobileBottomNav';
import TabletNavRail from './components/TabletNavRail';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
const { FiMenu, FiSearch, FiChevronLeft, FiCheck } = FiIcons;

import {
  APP_VIEW_DASHBOARD,
  APP_VIEW_BOARD,
  APP_VIEW_LIST,
  APP_VIEW_TIMELINE,
  APP_VIEW_SETTINGS,
  APP_VIEW_BACKUP,
  APP_NAME,
  SCRIPT_WEB_APP_URL_KEY,
  DEFAULT_SCRIPT_WEB_APP_URL
} from './constants';

const PHONE_BREAKPOINT = 640;
const DESKTOP_BREAKPOINT = 1100;

const AppLayout = () => {
  const {
    viewMode, setViewMode, activeWorkspaceId, isPaletteOpen, setIsPaletteOpen, isAuthenticated,
    login, authLoading, authError, checkSessionExpiry, runSync,
    testScriptConnection, workspaces, notes, editingNote,
    selectedNoteIds, saveStatus, isSyncing, syncState, syncSettings, setSyncSettings,
    showArchived
  } = useStore();

  const configuredUrl = localStorage.getItem(SCRIPT_WEB_APP_URL_KEY) || syncSettings.scriptUrl || DEFAULT_SCRIPT_WEB_APP_URL;
  const [loginName, setLoginName] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [showSetup, setShowSetup] = useState(false);
  const [setupUrl, setSetupUrl] = useState(configuredUrl);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [viewportW, setViewportW] = useState(() => window.innerWidth);
  const isPhone = viewportW < PHONE_BREAKPOINT;
  const isTablet = viewportW >= PHONE_BREAKPOINT && viewportW < DESKTOP_BREAKPOINT;
  const isDesktop = viewportW >= DESKTOP_BREAKPOINT;
  const isCompact = !isDesktop;
  const isNoteOpen = !!editingNote;
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= DESKTOP_BREAKPOINT);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setViewportW(w);
      setSidebarOpen(w >= DESKTOP_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const inZoomZone = target => target?.closest?.('[data-allow-zoom="true"]');
    const preventGesture = e => {
      if (!inZoomZone(e.target)) e.preventDefault();
    };
    const preventZoomWheel = e => {
      if ((e.ctrlKey || e.metaKey) && !inZoomZone(e.target)) e.preventDefault();
    };
    const preventZoomKeys = e => {
      if (!(e.ctrlKey || e.metaKey) || inZoomZone(e.target)) return;
      if (['+', '-', '=', '0'].includes(e.key)) e.preventDefault();
    };

    document.addEventListener('gesturestart', preventGesture, { passive: false });
    document.addEventListener('gesturechange', preventGesture, { passive: false });
    window.addEventListener('wheel', preventZoomWheel, { passive: false });
    window.addEventListener('keydown', preventZoomKeys, true);
    return () => {
      document.removeEventListener('gesturestart', preventGesture);
      document.removeEventListener('gesturechange', preventGesture);
      window.removeEventListener('wheel', preventZoomWheel);
      window.removeEventListener('keydown', preventZoomKeys, true);
    };
  }, []);

  useEffect(() => {
    const handleCheck = () => {
      checkSessionExpiry();
      if (isAuthenticated && document.visibilityState === 'visible') runSync('activation');
    };
    document.addEventListener('visibilitychange', handleCheck);
    window.addEventListener('focus', handleCheck);
    window.addEventListener('online', handleCheck);
    return () => {
      document.removeEventListener('visibilitychange', handleCheck);
      window.removeEventListener('focus', handleCheck);
      window.removeEventListener('online', handleCheck);
    };
  }, [isAuthenticated, checkSessionExpiry, runSync]);

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    const success = await login(loginName, loginPass);
    if (!success && authError === 'MISSING_SCRIPT_URL') setShowSetup(true);
  };

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      await testScriptConnection(setupUrl.trim());
      setTestResult({ ok: true, message: 'Connection successful!' });
    } catch (err) {
      setTestResult({ ok: false, message: err.message });
    } finally {
      setIsTesting(false);
    }
  };

  const handleSetupSave = (e) => {
    e.preventDefault();
    if (!setupUrl.trim().startsWith('https://')) return;
    setSyncSettings({ scriptUrl: setupUrl.trim() });
    setShowSetup(false);
  };

  const onNavigate = useCallback(() => {
    if (isCompact) setSidebarOpen(false);
  }, [isCompact]);

  const getViewTitle = () => {
    if (isNoteOpen) return editingNote.title || 'Note';
    switch (viewMode) {
      case APP_VIEW_DASHBOARD: return 'Dashboard';
      case APP_VIEW_SETTINGS: return 'Settings';
      case APP_VIEW_BACKUP: return 'Backup Vault';
      case APP_VIEW_LIST: return 'List';
      case APP_VIEW_TIMELINE: return 'Timeline';
      default: {
        const ws = workspaces.find(w => w.id === activeWorkspaceId);
        return ws ? ws.name : 'Notes';
      }
    }
  };

  const getStatusText = () => {
    if (!navigator.onLine) return 'Offline — saved locally';
    switch (saveStatus) {
      case 'saving': return 'Saving…';
      case 'saved': return 'Saved locally';
      case 'syncing': return 'Syncing…';
      case 'synced': return 'Synced';
      case 'error': return syncState.error || 'Sync error';
      default: return '';
    }
  };

  const handleMobileDone = () => {
    window.dispatchEvent(new CustomEvent('stickyhq:editor-done'));
  };

  const renderMainView = (activeNotes) => {
    switch (viewMode) {
      case APP_VIEW_DASHBOARD: return <Dashboard />;
      case APP_VIEW_LIST: return <ListView notes={activeNotes} />;
      case APP_VIEW_TIMELINE: return <TimelineView notes={activeNotes} />;
      case APP_VIEW_SETTINGS: return <Settings />;
      case APP_VIEW_BACKUP: return <BackupVault />;
      case APP_VIEW_BOARD:
      default: return <BoardView notes={activeNotes} />;
    }
  };

  if (showSetup) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 md:p-6 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-100 via-gray-50 to-gray-100">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-lg">
          <div className="bg-white rounded-3xl md:rounded-[2.5rem] shadow-2xl border border-white p-6 md:p-10 space-y-7 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-indigo-500" />
            <div className="text-center space-y-2">
              <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center text-white mx-auto shadow-xl shadow-indigo-200 mb-5">
                <SafeIcon name="Settings" className="text-3xl" />
              </div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">Sync Configuration</h1>
              <p className="text-gray-500 font-medium leading-relaxed">Use the deployed Google Apps Script endpoint for login and cross-device sync.</p>
            </div>
            <div className="space-y-5">
              <div className="space-y-2">
                <div className="flex justify-between items-center mb-1">
                  <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Apps Script URL</label>
                  <button onClick={handleTestConnection} disabled={isTesting || !setupUrl} className="text-xs font-bold text-blue-600 hover:text-blue-700 disabled:opacity-30">
                    {isTesting ? 'Testing…' : 'Test Connection'}
                  </button>
                </div>
                <textarea placeholder="https://script.google.com/macros/s/.../exec" value={setupUrl} onChange={e => { setSetupUrl(e.target.value); setTestResult(null); }} className="w-full px-4 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-indigo-500 focus:bg-white outline-none transition-all font-mono text-xs h-32 resize-none" />
                <AnimatePresence>
                  {testResult && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`p-4 rounded-xl text-xs font-bold flex items-start gap-3 ${testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      <SafeIcon name={testResult.ok ? 'CheckCircle' : 'AlertCircle'} className="mt-0.5 shrink-0" />
                      <span>{testResult.message}</span>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowSetup(false)} className="flex-1 py-3.5 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-2xl font-bold">Cancel</button>
                <button onClick={handleSetupSave} className="flex-[1.4] py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-bold shadow-lg">Save & Continue</button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 md:p-6 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-100 via-gray-50 to-gray-100">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="bg-white rounded-3xl md:rounded-[2.5rem] shadow-2xl border border-white p-6 md:p-10 space-y-7 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-400 to-indigo-500" />
            <div className="text-center space-y-2">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center text-white mx-auto shadow-xl shadow-blue-200 mb-5">
                <SafeIcon name="Book" className="text-3xl" />
              </div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 tracking-tight">{APP_NAME}</h1>
              <p className="text-sm text-gray-500">Fast cross-device notes with offline local storage.</p>
            </div>
            <form onSubmit={handleLoginSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Your Name</label>
                <div className="relative">
                  <SafeIcon name="User" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input autoComplete="username" type="text" placeholder="Enter your name" value={loginName} onChange={e => setLoginName(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-blue-500 font-medium outline-none" />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">Passcode</label>
                <div className="relative">
                  <SafeIcon name="Lock" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input autoComplete="current-password" type="password" inputMode="numeric" maxLength={6} placeholder="4-6 digit passcode" value={loginPass} onChange={e => setLoginPass(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-gray-50 border-2 border-gray-100 rounded-2xl focus:border-blue-500 font-medium tracking-[0.35em] outline-none" />
                </div>
              </div>
              <AnimatePresence>
                {authError && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} className="bg-red-50 text-red-600 p-4 rounded-xl text-xs font-bold flex items-start gap-2 border border-red-100">
                    <SafeIcon name="AlertCircle" className="mt-0.5 shrink-0" />
                    <span>{authError}</span>
                  </motion.div>
                )}
              </AnimatePresence>
              <button type="submit" disabled={authLoading || !loginName.trim() || !loginPass.trim()} className="w-full py-4 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-2xl font-bold text-lg shadow-xl shadow-blue-200 transition-all">
                {authLoading ? 'Unlocking…' : 'Unlock Workspace'}
              </button>
              <button type="button" onClick={() => setShowSetup(true)} className="w-full text-xs font-bold text-gray-400 hover:text-blue-600">Change sync endpoint</button>
            </form>
          </div>
        </motion.div>
      </div>
    );
  }

  const activeNotes = notes.filter(n => !n.deletedAt && n.workspaceId === activeWorkspaceId && (!n.archivedAt || showArchived));
  const isNotesView = [APP_VIEW_BOARD, APP_VIEW_LIST, APP_VIEW_TIMELINE].includes(viewMode);

  return (
    <div className="flex h-[100dvh] bg-gray-100 overflow-hidden font-sans text-gray-900 selection:bg-blue-100 w-full max-w-full overflow-x-hidden">
      <AnimatePresence>
        {isCompact && sidebarOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60]" onClick={() => setSidebarOpen(false)} />
        )}
      </AnimatePresence>

      <motion.div
        className={`${isCompact ? 'fixed inset-y-0 left-0 z-[90] shadow-2xl' : 'relative h-full'} shrink-0`}
        initial={isCompact ? { x: '-100%' } : false}
        animate={isCompact ? { x: sidebarOpen ? 0 : '-100%' } : { x: 0 }}
      >
        <Sidebar onNavigate={onNavigate} />
      </motion.div>

      {isTablet && <TabletNavRail onMore={() => setSidebarOpen(true)} />}

      <div className="flex-1 flex flex-col min-w-0 w-full overflow-x-hidden">
        {isCompact && (
          <>
            <header className="min-h-[calc(56px+env(safe-area-inset-top))] bg-white/90 backdrop-blur border-b border-gray-200 flex items-center px-3 sticky top-0 z-50 safe-top shrink-0">
              {isNoteOpen ? (
                <button onClick={handleMobileDone} className="min-w-11 min-h-11 p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition" aria-label="Back">
                  <SafeIcon icon={FiChevronLeft} className="text-2xl" />
                </button>
              ) : isTablet ? (
                <button onClick={() => setSidebarOpen(true)} className="min-w-11 min-h-11 p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition" aria-label="Open navigation">
                  <SafeIcon icon={FiMenu} className="text-2xl" />
                </button>
              ) : (
                <div className="w-11 h-11 flex items-center justify-center" aria-hidden="true">
                  <div className="w-8 h-8 rounded-xl bg-amber-400 flex items-center justify-center text-gray-900"><SafeIcon name="FileText" /></div>
                </div>
              )}
              <div className="flex-1 text-center font-bold text-gray-900 truncate px-2 flex flex-col justify-center min-w-0">
                <span className="leading-tight truncate">{getViewTitle()}</span>
                {isNoteOpen && (
                  <span className={`text-[10px] font-bold uppercase tracking-widest truncate ${saveStatus === 'error' ? 'text-red-500' : 'text-gray-400'}`}>{getStatusText()}</span>
                )}
              </div>
              {isNoteOpen ? (
                <button onClick={handleMobileDone} className="min-h-11 flex items-center gap-1 px-3 py-2 text-blue-600 font-bold text-sm hover:bg-blue-50 rounded-xl transition">
                  <SafeIcon icon={FiCheck} className="text-xl" /><span>Done</span>
                </button>
              ) : (
                <button onClick={() => setIsPaletteOpen(true)} className="min-w-11 min-h-11 p-2 text-gray-600 hover:bg-gray-100 rounded-xl transition">
                  <SafeIcon icon={FiSearch} className="text-xl" />
                </button>
              )}
            </header>
            {!isNoteOpen && isNotesView && (
              <div className="bg-white border-b border-gray-200 px-3 py-2 flex gap-1 shrink-0">
                {[
                  [APP_VIEW_BOARD, 'Grid', 'Board'],
                  [APP_VIEW_LIST, 'List', 'List'],
                  [APP_VIEW_TIMELINE, 'Clock', 'Timeline']
                ].map(([id, icon, label]) => (
                  <button key={id} onClick={() => setViewMode(id)} className={`flex-1 min-h-11 py-2 rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 ${viewMode === id ? 'bg-blue-50 text-blue-700' : 'text-gray-500'}`}>
                    <SafeIcon name={icon} /> {label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}

        {isDesktop && <TopBar />}

        <main className={`flex-1 overflow-y-auto overflow-x-hidden ${isPhone ? 'px-3 py-4 pb-28' : isTablet ? 'px-6 py-6 md:px-7 md:py-7' : 'p-6 md:p-8'} safe-bottom ${selectedNoteIds.length > 0 ? 'pb-32' : ''}`}>
          <div className={`${[APP_VIEW_SETTINGS, APP_VIEW_BACKUP].includes(viewMode) ? 'max-w-4xl' : 'max-w-7xl'} mx-auto min-h-full min-w-0`}>
            {renderMainView(activeNotes)}
          </div>
        </main>
      </div>

      <Editor />
      <CommandPalette />
      <BulkActionBar isMobile={isPhone} />
      <QuickCapture isMobile={isPhone} showFab={isDesktop} />

      {isPhone && !isNoteOpen && !isPaletteOpen && selectedNoteIds.length === 0 && (
        <MobileBottomNav onMore={() => setSidebarOpen(true)} />
      )}

      {isSyncing && isPhone && !isNoteOpen && (
        <div className="fixed bottom-[calc(78px+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-[65] bg-gray-900 text-white text-xs font-bold px-3 py-2 rounded-full shadow-lg">Syncing…</div>
      )}
    </div>
  );
};

export default AppLayout;